import {
  formatDuration,
  HOME_CLASS_COUNTDOWN_MAX_MINUTES,
  HOME_CLASS_IMMINENT_MAX_MINUTES,
  HOME_CLASS_TIMING_STATES,
  HOME_SHUTTLE_URGENT_MAX_MINUTES,
} from './homeTimePolicy'

export function getHeroMotionMode(hero) {
  if (hero?.type === 'shuttle') return 'shuttle'
  if (hero?.type === 'meal') return 'meal'
  if (hero?.type?.startsWith('class')) return 'class'
  if (hero?.type === 'weekend-empty') return 'weekend'
  return 'planner'
}

export function isHeroMotionUrgent(hero) {
  if (hero?.type === 'class-upcoming') return (hero.remainingMin ?? 99) <= HOME_CLASS_IMMINENT_MAX_MINUTES
  if (hero?.type === 'shuttle') {
    if (hero.meta === '곧') return true
    const mins = Number(String(hero.meta ?? '').match(/^(\d+)분 뒤$/)?.[1])
    return Number.isFinite(mins) && mins <= HOME_SHUTTLE_URGENT_MAX_MINUTES
  }
  return false
}

export const EVENING_DONE_HERO_COPY = Object.freeze({
  mainMessage: '오늘도 수고했어요',
  infoLine: '편안한 저녁 보내세요',
})

// ── 기본형 실시간 히어로 카드 ────────────────────────────────────────────

// ── 모드 → 카드 데이터 매핑 ───────────────────────────────────────────────
//
// HeroBrain이 만든 hero(type/context/...)를 보고 "어떤 모드인지" 키 하나로
// 줄인 뒤, 모드별 데이터({ variant, mainMessage, infoLine, ctaLabel,
// mascotExpression })만 채워서 HeroCard(HomeUnifiedHero)에 넘긴다.
// 모드가 늘어나도 JSX를 새로 만들 필요 없이 이 매핑에 항목만 추가하면 된다.

export function getHeroCardMode(hero, nightSkyHero = false) {
  if (hero?.type === 'class-ongoing') return 'class-ongoing'
  if (hero?.type === 'personal-ongoing') return 'personal-ongoing'
  if (hero?.type === 'personal-soon') return 'personal-soon'
  if (hero?.type === 'personal-upcoming') return 'personal-upcoming'
  if (hero?.type === 'class-upcoming' || hero?.type === 'class-next') {
    if (hero.timingState === HOME_CLASS_TIMING_STATES.IMMINENT || (hero.timingState == null && hero.remainingMin != null && hero.remainingMin <= HOME_CLASS_IMMINENT_MAX_MINUTES)) return 'class-soon'
    if (hero.timingState === HOME_CLASS_TIMING_STATES.COUNTDOWN || (hero.timingState == null && hero.remainingMin != null && hero.remainingMin <= HOME_CLASS_COUNTDOWN_MAX_MINUTES)) return 'class-countdown'
    if (hero.context === 'break') return 'class-break'
    if (hero.context === 'gap') return 'class-gap'
    if (hero.timingState === HOME_CLASS_TIMING_STATES.FAR_FIRST || hero.context === 'first-class') return 'class-first'
    if (hero.timingState === HOME_CLASS_TIMING_STATES.FAR_NEXT) return 'class-far-next'
    if (hero.context === 'early') return nightSkyHero ? 'class-early-night' : 'class-first'
    return 'class-far-next'
  }
  if (hero?.type === 'shuttle') return 'shuttle'
  if (hero?.type === 'meal') return 'meal'
  if (hero?.meta === '시간표 등록하기 ›') return 'empty-timetable'
  if (hero?.type === 'weekend-empty') return 'weekend-empty'
  return 'default'
}

// 모드별 카피(mainMessage/infoLine)만 채운다 — 레이아웃은 전부 HomeUnifiedHero가 그린다.
// mainMessage(=statusLabel)는 "{이름}님,"과 함께 line-clamp:2로 잘리므로 시간·과목명·분 단위
// 숫자 등 가변 길이 정보는 절대 넣지 않는다. 그런 정보는 모두 infoLine(=subLine)에 둔다.
export const HERO_CARD_COPY_BUILDERS = {
  // 남은 시간 포맷: 진행 중인 일정은 남은 시간, 다음 일정은 정책에 따른 의미 상태를 표시한다.
  'personal-ongoing': hero => ({
    mainMessage: '지금 일정이 진행 중이에요',
    infoLine: [hero.title, hero.subtitle, hero.remainingMin != null ? `${formatDuration(hero.remainingMin)} 남음` : null].filter(Boolean).join(' · '),
  }),
  'personal-soon': hero => ({
    mainMessage: '곧 일정이 시작돼요',
    infoLine: [hero.title, hero.subtitle].filter(Boolean).join(' · '),
  }),
  'personal-upcoming': hero => ({
    mainMessage: '다음 일정',
    infoLine: [hero.title, hero.subtitle].filter(Boolean).join(' · '),
  }),
  'class-ongoing': hero => ({
    mainMessage: '지금 수업 중이에요',
    infoLine: [hero.title, hero.room, hero.remainingMin != null ? `${formatDuration(hero.remainingMin)} 남음` : hero.meta].filter(Boolean).join(' · '),
  }),
  'class-first': hero => ({
    mainMessage: `오늘은 ${hero.meta?.split(' - ')[0] ?? '첫 수업'}부터 시작해요`,
    infoLine: [hero.title, hero.room].filter(Boolean).join(' · '),
  }),
  'class-countdown': hero => ({
    mainMessage: `수업까지 ${formatDuration(hero.remainingMin)}`,
    infoLine: [hero.meta?.split(' - ')[0], hero.title, hero.room].filter(Boolean).join(' · '),
  }),
  'class-far-next': hero => ({
    mainMessage: `다음 수업은 ${hero.meta?.split(' - ')[0] ?? '예정'}이에요`,
    infoLine: [hero.title, hero.room].filter(Boolean).join(' · '),
  }),
  'class-break': hero => ({
    mainMessage: '지금은 쉬는 시간이에요',
    infoLine: hero.remainingMin != null
      ? [`다음 수업 ${formatDuration(hero.remainingMin)} 뒤`, hero.title, hero.room].filter(Boolean).join(' · ')
      : [hero.title, hero.room].filter(Boolean).join(' · '),
  }),
  'class-gap': hero => ({
    mainMessage: '지금은 공강이에요',
    infoLine: hero.remainingMin != null
      ? [`다음 수업 ${formatDuration(hero.remainingMin)} 뒤`, hero.title, hero.room].filter(Boolean).join(' · ')
      : [hero.title, hero.room].filter(Boolean).join(' · '),
  }),
  'class-early': hero => ({
    mainMessage: `오늘은 ${hero.meta?.split(' - ')[0] ?? '첫 수업'}부터 시작해요`,
    infoLine: [hero.meta, hero.title, hero.room].filter(Boolean).join(' · '),
  }),
  // '밤 · 첫 수업 미리보기' 전용 — class-early와 같은 type/context(class-next, early)지만
  // nightSkyHero(밤 화면에서 내일 첫 수업을 미리 보여줌)일 때만 이 모드로 분기된다.
  'class-early-night': hero => ({
    mainMessage: '내일 첫 수업을 준비해보세요',
    infoLine: [hero.meta, hero.title, hero.room].filter(Boolean).join(' · '),
  }),
  'class-soon': hero => ({
    mainMessage: '곧 수업이 시작돼요',
    infoLine: [hero.title, hero.room].filter(Boolean).join(' · '),
  }),
  'class-next': hero => ({
    mainMessage: `다음 수업을 준비해요${hero.meta?.split(' - ')[0] ? ` · ${hero.meta.split(' - ')[0]}` : ''}`,
    infoLine: [hero.meta?.split(' - ')[0], hero.title, hero.room].filter(Boolean).join(' · '),
  }),
  shuttle: hero => {
    const mainMessage = hero.context === 'go-home'
      ? '이제 집에 갈 시간이에요'
      : hero.context === 'commute'
      ? '오늘도 등교할 시간이에요'
      : '셔틀이 곧 출발해요'
    const shuttleMeta = hero.meta && hero.meta !== '학기 중 평일 운행 기준' ? hero.meta : null
    return { mainMessage, infoLine: [hero.subtitle, hero.title, shuttleMeta].filter(Boolean).join(' · ') }
  },
  meal: () => ({
    mainMessage: '지금 밥 먹기 좋아요',
    infoLine: '학생식당 운영 중',
  }),
  'empty-timetable': () => ({
    mainMessage: '시간표를 등록해보세요',
    infoLine: '수업·셔틀·학식을 한눈에',
  }),
  'weekend-empty': () => ({
    mainMessage: '오늘은 쉬는 날이에요',
    infoLine: '여유로운 주말을 보내세요',
  }),
  default: hero => {
    const isFreeDay = hero?.label === '오늘 남은 수업 없음'
    if (isFreeDay) {
      const hasNextFuture = hero.title && hero.title !== '오늘 수업은 마무리됐어요'
      return {
        mainMessage: '오늘은 수업이 없어요',
        infoLine: hasNextFuture ? [hero.title, hero.meta].filter(Boolean).join(' · ') : '여유로운 하루를 보내도 좋아요',
      }
    }
    if (hero?.context === 'evening-done') {
      return EVENING_DONE_HERO_COPY
    }
    if (hero?.label === '셔틀 운행 없음') {
      return {
        mainMessage: '오늘은 셔틀이 없어요',
        infoLine: hero?.subtitle ?? '오늘은 셔틀이 운행하지 않아요',
      }
    }
    return { mainMessage: hero?.title ?? '오늘도 화이팅!', infoLine: hero?.subtitle ?? '' }
  },
}

export function getHeroCardCtaLabel(hero, mode) {
  if (mode === 'shuttle') return '셔틀 보기'
  if (mode === 'meal') return '오늘 메뉴 보기'
  if (mode === 'empty-timetable') return '시간표 등록하기'
  return '시간표 보기'
}

// 마스코트 표정 — variant 위치(우하단)는 항상 같고, 모드별로 PNG만 바뀐다.
export function getHeroCardMascotExpression(hero, mode) {
  switch (mode) {
    case 'class-ongoing':
    case 'class-soon':
    case 'class-next':
    case 'personal-ongoing':
    case 'personal-soon':
      return 'looking'
    case 'class-first':
    case 'class-early':
    case 'class-early-night':
    case 'personal-upcoming':
      return 'welcome'
    case 'class-break':
    case 'class-gap':
    case 'weekend-empty':
      return 'sleepy'
    case 'shuttle':
      return hero?.context === 'go-home' ? 'success' : 'welcome'
    case 'meal':
      return 'neutral'
    case 'empty-timetable':
      return 'looking'
    default:
      return hero?.context === 'evening-done' ? 'success' : 'neutral'
  }
}

export function buildHeroCardData(hero, nightSkyHero = false) {
  const safeHero = hero ?? {}
  const mode = getHeroCardMode(safeHero, nightSkyHero)
  const builder = HERO_CARD_COPY_BUILDERS[mode] ?? HERO_CARD_COPY_BUILDERS.default
  const { mainMessage, infoLine } = builder(safeHero)
  return {
    variant: mode === 'shuttle' ? 'data' : 'message',
    mainMessage,
    infoLine,
    ctaLabel: getHeroCardCtaLabel(safeHero, mode),
    mascotExpression: getHeroCardMascotExpression(safeHero, mode),
  }
}
